import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AppState } from '../../../../app.reducers';
import { Observable } from 'rxjs';
import {
  LoadFinancialHealthReportAction, LoadRealTimeFinancialHealthReportAction
} from '../../../reports/redux/reports.actions';
import { getFinancialHealthReport, getRealTimeFinancialHealthReport } from '../../../reports/redux/reports.selectors';
import { FinancialHealthReport, FinancialHealthReportRequest, RealTimeFinancialHealthReport, RealTimeFinancialHealthReportRequest } from '../../../reports/shared/models';
import { getSelectedProperty, getSelectedFiscalYear } from '../../../property/property-store/property.selectors';
import { Property } from '../../../property/shared/models';
import { FiscalYear, getFiscalYearStartAndEndDates } from 'app/internal/shared/models';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { SetBackToDashboard } from '../../../component/component-store';
import { Transaction, TransactionFilter, TransactionRequest } from 'app/internal/transactions/shared/models';
import { selectTransactions } from 'app/internal/transactions/transactions-store/transactions.selectors';
import * as TransactionActions from '../../../transactions/transactions-store/transactions.actions';
import dayjs from 'dayjs';
import { LoaderService } from 'app/shared/services/api/loader.service';
import { getGeneralLedgerFundConfigs } from '../../../connected-apps/store/connected-apps.selectors';
import { GeneralLedgerFundConfigRequest } from '../../../connected-apps/models/general-ledger-fund-config-request';
import * as ConnectedAppsActions from '../../../connected-apps/store/connected-apps.actions';
import { ExternalLedgerFund } from '../../../connected-apps/models/general-ledger-fund-config';
import { GeneralLedgerConfigRequest } from 'app/internal/connected-apps/models';
import { IntegrationProperty } from 'app/internal/admin/shared/models/integration-property';
import { getIntegrationProperty } from 'app/internal/admin/admin-store/admin.selectors';
import { LoadAdminIntegrationPropertyAction } from 'app/internal/admin/admin-store';
import { AccountsActions } from 'app/internal/accounts/store/accounts.actions';
import { selectFinancialAccounts } from 'app/internal/accounts/store/accounts.selectors';
import { FinancialAccount } from 'app/internal/accounts/shared/models/financial-account.model';
import { FinancialAccountListRequest } from 'app/internal/accounts/shared/models/financial-account-list-request';

@Component({
  selector: 'sp-dashboard-current',
  templateUrl: './dashboard-current.component.html',
  styleUrls: ['./dashboard-current.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DashboardCurrentComponent implements OnInit, OnDestroy {

  financialHealthReport$: Observable<FinancialHealthReport>;
  realTimeFinancialHealthReport$: Observable<RealTimeFinancialHealthReport>;
  property$: Observable<Property>;
  selectedFiscalYear$: Observable<FiscalYear>;
  propertyId: string;
  fiscalYear: FiscalYear;
  fiscalYearStartDate: Date;
  showIntegrationDashboard = false;
  private subs = new SubSink();
  transactions$: Observable<Transaction[]>;
  financialAccounts$: Observable<FinancialAccount[]>;
  generalLedgerFundConfigs$: Observable<ExternalLedgerFund[]>;
  integrationProperty$: Observable<IntegrationProperty>;

  constructor(
    private store: Store<AppState>,
    public loaderService: LoaderService
  ) {
    this.property$ = this.store.pipe(select(getSelectedProperty));
    this.selectedFiscalYear$ = this.store.pipe(select(getSelectedFiscalYear));
    this.financialHealthReport$ = this.store.pipe(select(getFinancialHealthReport));
    this.realTimeFinancialHealthReport$ = this.store.pipe(select(getRealTimeFinancialHealthReport));
    this.transactions$ = this.store.pipe(select(selectTransactions));
    this.financialAccounts$ = this.store.pipe(select(selectFinancialAccounts));
    this.generalLedgerFundConfigs$ = this.store.pipe(select(getGeneralLedgerFundConfigs));
    this.integrationProperty$ = this.store.pipe(select(getIntegrationProperty));
  }

  ngOnInit() {

    this.subs.sink = combineLatest([this.selectedFiscalYear$, this.property$])
      .pipe(filter(([fy, p]) => fy != null && p != null && !fy.requireSetup && fy.isCurrentFiscalYear))
      .subscribe(([fiscalYear, property]) => {

        this.fiscalYear = fiscalYear;
        this.propertyId = property.propertyId;

        if (property.isIntegratedProperty && fiscalYear.isCurrentFiscalYear) {

          const reportRequest = new RealTimeFinancialHealthReportRequest(property.propertyId, fiscalYear.year);
          this.store.dispatch(new LoadRealTimeFinancialHealthReportAction(reportRequest));

          // The Dashboard only needs Transactions for the last 12 months (or the full selected fY)  so add
          // those filters here...
          const transactionFilter = new TransactionFilter();

          //Find min of fiscalYearStartDate & one year ago
          const fyDateTuple = getFiscalYearStartAndEndDates(this.fiscalYear);
          const startDates = [fyDateTuple[0], dayjs().subtract(12, 'month').toDate()];
          const minDate = startDates.reduce(function (a, b) {
            return a < b ? a : b;
          });

          transactionFilter.startDate = minDate;
          const transactionRequest = new TransactionRequest(this.propertyId, transactionFilter);
          this.store.dispatch(TransactionActions.getTransactions({ transactionRequest }));

          this.showIntegrationDashboard = true;

          this.store.dispatch(ConnectedAppsActions.loadExternalLedgers({ payload: new GeneralLedgerConfigRequest(property.propertyId) }));
          this.store.dispatch(ConnectedAppsActions.loadExternalLedgerFunds({ payload: new GeneralLedgerFundConfigRequest(property.propertyId) }));
          this.store.dispatch(AccountsActions.loadFinancialAccounts({ payload: new FinancialAccountListRequest(property.propertyId) }));
          this.store.dispatch(new LoadAdminIntegrationPropertyAction(property.propertyId));
        } else {
          const reportRequest = new FinancialHealthReportRequest(property.propertyId, fiscalYear.year);
          this.store.dispatch(new LoadFinancialHealthReportAction(reportRequest));
        }
      });


  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  onBackToDashboard() {
    this.store.dispatch(new SetBackToDashboard(true));
  }

  onGeneratePdfStarted() {
    // Shows the loading spinner overlay...    
    this.loaderService.showLoader();
  }

  onGeneratePdfFinished() {
    // Hides the loading spinner overlay...    
    this.loaderService.hideLoader();

    // Just fully reload the Integration Dashboard so the charts get re-rendered correctly
    location.reload();
  }
}
